import { createSlice } from '@reduxjs/toolkit'
import {
  fetchAllSurveys,
  updateSurveyStatus,
  fetchSurveyResponses,
  createSurveyResponse,
  fetchAllSurveyResponses,
  fetchTotalSurveyResponses,
  fetchSurveyResponsesMoodScale
} from '../actions/surveyActions.js'

// Helper function to apply filters similar to reviews
const applySurveyFilters = (responses, filters) => {
  // Ensure responses is an array before filtering
  if (!responses || !Array.isArray(responses)) {
    return []
  }

  return responses.filter(response => {
    // Ensure response object exists
    if (!response) return false

    // Filter by user type
    if (filters.userType && filters.userType !== 'allUsers' && response.userType !== filters.userType) {
      return false
    }

    // Filter by survey type
    if (filters.surveyType && filters.surveyType !== 'appExperience') {
      const responseType = response.surveyType?.toLowerCase().replace(' ', '') || ''
      if (responseType !== filters.surveyType.toLowerCase()) {
        return false
      }
    }

    // Filter by answer/rating
    if (filters.answer && response.answer !== filters.answer) {
      return false
    }

    // Filter by date range
    if (filters.dateRange && filters.dateRange.startDate && filters.dateRange.endDate) {
      const responseDate = new Date(response.createdAt || response.date || response.updatedAt)
      const startDate = new Date(filters.dateRange.startDate)
      const endDate = new Date(filters.dateRange.endDate)

      // Set time boundaries
      startDate.setHours(0, 0, 0, 0)
      endDate.setHours(23, 59, 59, 999)

      if (responseDate < startDate || responseDate > endDate) {
        return false
      }
    }

    // Filter by search term (search in comment)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      const commentMatch = response.comment?.toLowerCase().includes(searchLower)
      if (!commentMatch) {
        return false
      }
    }

    return true
  })
}

const initialState = {
  allSurveys: [],
  surveyResponses: {},
  allSurveyResponses: [],
  totalResponses: 0,
  moodScaleData: null,
  filters: {
    userType: 'allUsers',
    surveyType: 'appExperience',
    answer: '',
    dateRange: null,
    searchTerm: ''
  },
  filteredResponses: [],
  loading: false,
  error: null,
}

const surveySlice = createSlice({
  name: 'surveys',
  initialState,
  reducers: {
    clearSurveyError: (state) => {
      state.error = null
    },
    clearSurveyData: (state) => {
      state.allSurveys = []
      state.surveyResponses = {}
      state.allSurveyResponses = []
      state.filteredResponses = []
      state.totalResponses = 0
      state.moodScaleData = null
      state.error = null
    },
    setSurveyFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
      // Apply filters immediately
      state.filteredResponses = applySurveyFilters(state.allSurveyResponses, state.filters)
    },
    clearSurveyFilters: (state) => {
      state.filters = {
        userType: 'allUsers',
        surveyType: 'appExperience',
        answer: '',
        dateRange: null,
        searchTerm: ''
      }
      state.filteredResponses = state.allSurveyResponses
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all surveys
      .addCase(fetchAllSurveys.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllSurveys.fulfilled, (state, action) => {
        state.loading = false
        const surveysData = action.payload?.data || action.payload
        state.allSurveys = Array.isArray(surveysData) ? surveysData : []
      })
      .addCase(fetchAllSurveys.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update survey status
      .addCase(updateSurveyStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateSurveyStatus.fulfilled, (state, action) => {
        state.loading = false
        // Update survey status in allSurveys if needed
      })
      .addCase(updateSurveyStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch survey responses for specific survey
      .addCase(fetchSurveyResponses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSurveyResponses.fulfilled, (state, action) => {
        state.loading = false
        const { surveyId, responses } = action.payload
        const responsesData = responses?.data || responses
        state.surveyResponses[surveyId] = Array.isArray(responsesData) ? responsesData : []
      })
      .addCase(fetchSurveyResponses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Create survey response
      .addCase(createSurveyResponse.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createSurveyResponse.fulfilled, (state, action) => {
        state.loading = false
        const { surveyId, response } = action.payload
        const newResponse = response?.data || response

        // Add to specific survey responses if they exist
        if (state.surveyResponses[surveyId] && Array.isArray(state.surveyResponses[surveyId])) {
          state.surveyResponses[surveyId].unshift(newResponse)
        }

        // Add to all survey responses if it exists
        if (newResponse && Array.isArray(state.allSurveyResponses)) {
          state.allSurveyResponses.unshift(newResponse)
        }

        // Update filtered responses
        state.filteredResponses = applySurveyFilters(state.allSurveyResponses, state.filters)
      })
      .addCase(createSurveyResponse.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch all survey responses
      .addCase(fetchAllSurveyResponses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAllSurveyResponses.fulfilled, (state, action) => {
        state.loading = false
        const responsesData = action.payload?.data?.responses || action.payload?.data || action.payload
        state.allSurveyResponses = Array.isArray(responsesData) ? responsesData : []
        state.filteredResponses = applySurveyFilters(state.allSurveyResponses, state.filters)
      })
      .addCase(fetchAllSurveyResponses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch total survey responses
      .addCase(fetchTotalSurveyResponses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTotalSurveyResponses.fulfilled, (state, action) => {
        state.loading = false
        state.totalResponses = action.payload?.total || action.payload || 0
      })
      .addCase(fetchTotalSurveyResponses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch mood scale data
      .addCase(fetchSurveyResponsesMoodScale.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSurveyResponsesMoodScale.fulfilled, (state, action) => {
        state.loading = false
        state.moodScaleData = action.payload?.data || action.payload
      })
      .addCase(fetchSurveyResponsesMoodScale.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  clearSurveyError,
  clearSurveyData,
  setSurveyFilters,
  clearSurveyFilters
} = surveySlice.actions

export default surveySlice.reducer